import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { Tenant } from '@/types';

/**
 * PDFs de comprobantes de venta. Hay dos, y la diferencia es deliberada y visible:
 *
 *  - TICKET (no fiscal): comprobante interno de la venta. No sirve para ARCA.
 *  - FACTURA (fiscal): tiene CAE, vencimiento del CAE y el QR obligatorio.
 *
 * Se distinguen a simple vista porque el ticket lleva la leyenda "DOCUMENTO NO VÁLIDO
 * COMO FACTURA". Confundir uno con otro es el error caro acá.
 */

export interface ComprobanteItem {
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
}

export interface FacturaData {
  invoiceTypeLabel: string;      // "Factura C"
  letra: string;                 // "C"
  posNumber: number;
  invoiceNumber: number;
  fecha: string;
  cae: string;
  caeDueDate?: string | null;
  qrUrl?: string | null;
  emisorCuit?: string | null;
  emisorRazonSocial?: string | null;
  emisorIvaCondition?: string | null;
  receptorNombre?: string | null;
  receptorDoc?: string | null;
  receptorCondicion?: string | null;
  receptorDomicilio?: string | null;
  items: ComprobanteItem[];
  total: number;
}

export interface TicketData {
  numero: string | number;
  fecha: string;
  clienteNombre?: string | null;
  items: ComprobanteItem[];
  total: number;
  medioPago?: string | null;
}

const money = (n: number): string =>
  '$' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CONDICION_LABEL: Record<string, string> = {
  monotributo: 'Responsable Monotributo',
  responsable_inscripto: 'Responsable Inscripto',
  exento: 'Exento',
  consumidor_final: 'Consumidor Final',
  no_categorizado: 'No Categorizado',
};

export const condicionLabel = (v?: string | null): string =>
  (v && CONDICION_LABEL[v]) || 'Consumidor Final';


/** Encabezado común: datos de la veterinaria arriba de todo. */
function encabezado(doc: jsPDF, tenant: Tenant): number {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(40, 40, 40);
  doc.text(tenant.name, 15, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(110, 110, 110);
  let y = 24;
  if (tenant.address) { doc.text(tenant.address, 15, y); y += 4.5; }
  if (tenant.phone) { doc.text(`Tel: ${tenant.phone}`, 15, y); y += 4.5; }
  return y + 2;
}

/** Tabla de items, compartida por los dos comprobantes. */
function tablaItems(doc: jsPDF, items: ComprobanteItem[], ancho: number, y: number): number {
  doc.setFillColor(235, 235, 235);
  doc.rect(15, y, ancho - 30, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text('Descripción', 18, y + 5);
  doc.text('Cant.', ancho - 78, y + 5, { align: 'right' });
  doc.text('P. unit.', ancho - 52, y + 5, { align: 'right' });
  doc.text('Importe', ancho - 18, y + 5, { align: 'right' });
  y += 11;

  doc.setFont('helvetica', 'normal');
  for (const item of items) {
    if (y > 250) { doc.addPage(); y = 20; }
    doc.text(String(item.descripcion || '').substring(0, 50), 18, y);
    doc.text(String(item.cantidad), ancho - 78, y, { align: 'right' });
    doc.text(money(item.precioUnitario), ancho - 52, y, { align: 'right' });
    doc.text(money(item.subtotal), ancho - 18, y, { align: 'right' });
    y += 6;
  }
  return y;
}


/**
 * TICKET NO FISCAL. Comprobante interno de la venta.
 * Lleva la leyenda bien visible para que nadie lo confunda con una factura.
 */
export function generarTicketPdf(data: TicketData, tenant: Tenant): void {
  const doc = new jsPDF();
  const ancho = doc.internal.pageSize.getWidth();

  let y = encabezado(doc, tenant);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(40, 40, 40);
  doc.text('COMPROBANTE DE VENTA', ancho / 2, y + 4, { align: 'center' });

  // La aclaración que evita el malentendido
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(180, 60, 60);
  doc.text('DOCUMENTO NO VÁLIDO COMO FACTURA', ancho / 2, y + 10, { align: 'center' });
  y += 18;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text(`Nº ${data.numero}`, 15, y);
  doc.text(data.fecha, ancho - 15, y, { align: 'right' });
  y += 5;
  if (data.clienteNombre) { doc.text(`Cliente: ${data.clienteNombre}`, 15, y); y += 5; }
  if (data.medioPago) { doc.text(`Pago: ${data.medioPago}`, 15, y); y += 5; }
  y += 4;

  y = tablaItems(doc, data.items, ancho, y);

  y += 4;
  doc.setDrawColor(200, 200, 200);
  doc.line(ancho - 80, y, ancho - 15, y);
  y += 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(40, 40, 40);
  doc.text('TOTAL', ancho - 60, y, { align: 'right' });
  doc.text(money(data.total), ancho - 15, y, { align: 'right' });

  doc.save(`comprobante-${data.numero}.pdf`);
}


/**
 * FACTURA FISCAL. Lleva la letra del comprobante, el CAE con su vencimiento y el QR
 * obligatorio de ARCA (RG 4892). Sin CAE esto no se emite.
 */
export async function generarFacturaPdf(data: FacturaData, tenant: Tenant): Promise<void> {
  const doc = new jsPDF();
  const ancho = doc.internal.pageSize.getWidth();

  // Recuadro con la letra, como en las facturas de papel
  doc.setDrawColor(60, 60, 60);
  doc.setLineWidth(0.5);
  doc.rect(ancho / 2 - 9, 10, 18, 16);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.setTextColor(40, 40, 40);
  doc.text(data.letra, ancho / 2, 22, { align: 'center' });

  let y = encabezado(doc, tenant);

  // Datos del comprobante, a la derecha
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(40, 40, 40);
  doc.text(data.invoiceTypeLabel.toUpperCase(), ancho - 15, 18, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const nro = `${String(data.posNumber).padStart(5, '0')}-${String(data.invoiceNumber).padStart(8, '0')}`;
  doc.text(`Nº ${nro}`, ancho - 15, 24, { align: 'right' });
  doc.text(`Fecha: ${data.fecha}`, ancho - 15, 29, { align: 'right' });

  // Emisor
  if (data.emisorCuit) { doc.text(`CUIT: ${data.emisorCuit}`, 15, y); y += 4.5; }
  doc.text(condicionLabel(data.emisorIvaCondition), 15, y);
  y += 8;

  // Receptor
  doc.setDrawColor(210, 210, 210);
  doc.line(15, y, ancho - 15, y);
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text('Cliente', 15, y);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  y += 5;
  doc.text(data.receptorNombre || 'Consumidor Final', 15, y);
  if (data.receptorDoc) doc.text(`Doc: ${data.receptorDoc}`, ancho / 2, y);
  y += 5;
  doc.text(condicionLabel(data.receptorCondicion), 15, y);
  if (data.receptorDomicilio) doc.text(data.receptorDomicilio, ancho / 2, y);
  y += 8;

  y = tablaItems(doc, data.items, ancho, y);

  y += 4;
  doc.setDrawColor(200, 200, 200);
  doc.line(ancho - 80, y, ancho - 15, y);
  y += 7;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(40, 40, 40);
  doc.text('TOTAL', ancho - 60, y, { align: 'right' });
  doc.text(money(data.total), ancho - 15, y, { align: 'right' });
  y += 14;

  // QR + CAE al pie, que es lo que le da validez
  if (data.qrUrl) {
    try {
      const qr = await QRCode.toDataURL(data.qrUrl, { margin: 0, width: 300 });
      doc.addImage(qr, 'PNG', 15, y, 28, 28);
    } catch {
      /* si el QR falla, la factura igual sale con el CAE */
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text(`CAE Nº ${data.cae}`, 50, y + 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  if (data.caeDueDate) doc.text(`Vencimiento del CAE: ${data.caeDueDate}`, 50, y + 14);
  doc.text('Comprobante autorizado por ARCA', 50, y + 20);

  doc.save(`factura-${data.letra}-${nro}.pdf`);
}
